// @flow

import * as React from 'react';
import Box from 'sail/Box';
import {ContentHeader} from 'sail/Content';
import {ContentTab} from 'sail/ContentTabs';
import Toggle, {ToggleItem} from 'sail/Toggle';
import {useArticle, usePrefs, useLocation} from 'docs/data';
import {useContentAnimation} from 'docs/shell/content/ContentAnimationProvider';
import {removeNulls} from 'docs/util';

import type {Article} from 'docs/data';
import type {PageSelectorOption} from './PageSelectorOption';

import './AdvancedPageSelector.scss';

// Different sets of selector options may use different sets of prefs. As a result,
// when the user chooses an option, we un-set all page prefs which aren't explicitly
// used in the selector option's prefs.
const buildPagePrefs = (
  pagePrefs: Array<string>,
  values: {[pref: string]: string},
) => {
  return pagePrefs.reduce((hash, pref) => {
    hash[pref] = values[pref] || null;
    return hash;
  }, {});
};

const useActiveOption = (
  article: Article,
  options: Array<PageSelectorOption>,
) => {
  const {getPref} = usePrefs();
  const matchingOptions = article.pagePrefs.reduce((remainingOptions, pref) => {
    const value = getPref(pref);

    if (!value || remainingOptions.length === 1) {
      return remainingOptions;
    }

    const filteredOptions = remainingOptions.filter(
      (option) => option.prefs[pref] === value,
    );

    return filteredOptions.length === 0 ? remainingOptions : filteredOptions;
  }, options);

  return matchingOptions[0];
};

type SubselectorProps = {
  article: Article,
  options: Array<PageSelectorOption>,
};

function useUpdatePagePrefs() {
  const {article} = useArticle();
  const {setPrefs} = usePrefs();
  const {replaceLocation} = useLocation();

  return (optionPrefs) => {
    const prefs = buildPagePrefs(article.pagePrefs, optionPrefs);
    setPrefs(prefs);
    replaceLocation({query: removeNulls(prefs)});
  };
}

const Subselector = ({article, options}: SubselectorProps) => {
  const {setAnimationDirection} = useContentAnimation();
  const updatePrefs = useUpdatePagePrefs();

  const activeOption = useActiveOption(article, options);
  const activeIndex = options.indexOf(activeOption);

  const tabs = options.map((option, index) => (
    <ContentTab
      className="Subselector-tab"
      key={index}
      size="medium"
      active={option === activeOption}
      onClick={() => {
        setAnimationDirection(activeIndex, index);
        updatePrefs(option.prefs);
      }}
    >
      {option.title}
    </ContentTab>
  ));

  return (
    <ContentHeader size="medium" padding={{top: 16}} start={{content: tabs}} />
  );
};

type AdvancedPageSelectorProps = {
  options: Array<PageSelectorOption>,
};

export const AdvancedPageSelector = ({options}: AdvancedPageSelectorProps) => {
  const {article} = useArticle();
  const {setAnimationDirection} = useContentAnimation();
  const updatePrefs = useUpdatePagePrefs();

  const activeOption = useActiveOption(article, options);
  const activeIndex = options.indexOf(activeOption);

  return (
    <Box className="AdvancedPageSelector">
      <Toggle type="radio">
        {options.map((option, index) => (
          <ToggleItem
            key={index}
            label={option.title}
            checked={option === activeOption}
            onChange={() => {
              setAnimationDirection(activeIndex, index);
              updatePrefs(option.prefs);
            }}
          />
        ))}
      </Toggle>
      {activeOption.children && (
        <Subselector article={article} options={activeOption.children} />
      )}
    </Box>
  );
};
